import { useState } from "react";
import { useDispatch } from "react-redux";
import { When } from "react-if";
import { OrganisationFeatureFlags } from "shared";
import { SparklesIcon } from "@heroicons/react/24/outline";

import { Paragraph } from "../../../../../common/Atoms/Typography/Paragraph";
import { Divider } from "../../../../../common/Atoms/Divider";
import { Modal } from "../../../../../common/Atoms/Modal";
import { Button } from "../../../../../common/Atoms/Button";
import { TextArea } from "../../../../../common/Atoms/TextArea";
import { Notification } from "../../../../../common/Atoms/Notification";
import { useApiRequest } from "../../../../hooks/useApiRequest";
import { env } from "../../../../../common/lib/env";
import { useGetSelectedOrganisation } from "../../../../hooks/useGetSelectedOrganisation";
import { useUser } from "../../../../hooks/useUser";
import { open as openAssistant } from "../../../../slices/assistantDrawer";

export function SetUpForMe() {
  const [setupModalOpen, setSetupModalOpen] = useState(false);
  const [setupRequestText, setSetupRequestText] = useState(``);
  const apiRequest = useApiRequest();
  const [requestError, setRequestError] = useState<string | null>(null);
  const [requestLoading, setRequestLoading] = useState(false);
  const [setupRequestSuccess, setSetupRequestSuccess] = useState(false);
  const dispatch = useDispatch();
  const currentOrg = useGetSelectedOrganisation();
  const { data: user } = useUser();
  const assistantEnabled = !!currentOrg?.featureFlags?.includes(OrganisationFeatureFlags.AI_ASSISTANT) || !!user?.adminOverride;

  function tryAssistant() {
    setSetupModalOpen(false);
    dispatch(openAssistant(setupRequestText || undefined));
  }

  async function sendRequest() {
    setRequestError(null);

    if (!setupRequestText) {
      setRequestError(`Please enter a description of what you would like to automate.`);
      setRequestLoading(false);

      return;
    }

    if (setupRequestText.length > 5000) {
      setRequestError(`Your request is too long. Please limit it to 5000 characters. (${setupRequestText.length} / 5000)`);
      setRequestLoading(false);

      return;
    }

    try {
      setRequestLoading(true);

      await apiRequest({
        method: `POST`,
        url: `${env.apiUrl}/api/v1/organisation/setupRequest`,
        data: {
          setupRequestText,
        },
      });

      setSetupRequestSuccess(true);
      setSetupRequestText(``);
      setRequestLoading(false);
    }
    catch (err) {
      setSetupRequestSuccess(false);
      setRequestLoading(false);
      setRequestError(`Failed to send request. Please try again later.`);
      console.error(err);
    }
  }

  return (
    <>
      { /* Trigger buttons */ }
      <div className={ `flex gap-2` }>
        <Button
          color={ `paidniceGreenOutline` }
          onClick={ () => setSetupModalOpen(true) }
        >
          { `Set it up for me` }
        </Button>
      </div>
      <Modal
        open={ setupModalOpen }
        setOpen={ setSetupModalOpen }
        title={ `Request a member of the Paidnice team to help configure your account.` }
        width={ `xl` }
      >
        <div className={ `pl-4 rounded-md mb-4 bg-white` }>
          <Paragraph
            variant={ `help` }
          >
            { `Describe in as much detail as possible what you would like Paidnice to automate for you. Include any rules or criteria and we will do our best to configure them. E.g "Issue a late fee of 4% after 30 days overdue and the invoice value is greater than $100"` }
          </Paragraph>

          <Divider
            weight={ `light` }
            spacing={ `small` }
          />

          <When condition={ requestError }>
            <Notification
              type={ `error` }
              className={ `mt-2` }
            >
              { requestError }
            </Notification>
          </When>

          <When condition={ setupRequestSuccess }>
            <Notification
              type={ `success` }
              className={ `mt-2` }
            >
              { `Your request has been sent! We will get back to you as soon as possible.` }
            </Notification>
          </When>

          <When condition={ !setupRequestSuccess }>
            <When condition={ assistantEnabled }>
              <div className={ `mb-2 flex items-center justify-between gap-3 rounded-md border border-indigo-200 bg-indigo-50 px-3 py-2` }>
                <Paragraph size={ `sm` }>
                  { `Want it done now? The AI assistant can read your current setup and propose the policies for you to approve.` }
                </Paragraph>
                <Button
                  color={ `gray` }
                  size={ `sm` }
                  onClick={ tryAssistant }
                  className={ `shrink-0` }
                >
                  <SparklesIcon className={ `mr-1 inline h-4 w-4 text-indigo-600` } />
                  { `Try the AI assistant` }
                </Button>
              </div>
            </When>

            <Notification
              type={ `info` }
            >
              { `Nothing will be set live and sent to your customers until you have approved the setup.` }
            </Notification>

            <TextArea
              rows={ 5 }
              placeholder={ `Describe your ideal automation setup here...` }
              className={ `mt-2` }
              value={ setupRequestText }
              onChange={ e => setSetupRequestText(e.value?.toString() || ``) }
            />

            <div className={ `flex justify-end mt-4` }>
              <Button
                onClick={ sendRequest }
                disabled={ requestLoading || !setupRequestText }
                loading={ requestLoading }
              >
                { `Submit request` }
              </Button>
            </div>
          </When>
        </div>
      </Modal>
    </>
  );
}
